// ═══════════════════════════════════════════════════════════════════════════
// AI → EditorCommand translator — BASE-PRIMITIVE + BASE-MODIFY (CC-2c sub-arm 4)
// ═══════════════════════════════════════════════════════════════════════════
//
// Translates the base-primitive create shapes (LINE/CIRCLE/ARC/PLINE) and the
// base-modify shapes (MOVE/COPY/ROTATE) into the internal `EditorCommand`s the
// store dispatches. Every path runs the CC-2a fail-closed guard
// (`assertAiCallableBasePrimAction`) BEFORE producing any EditorCommand, so the
// safe contour rejects a non-AI-callable / unknown target (e.g. POINT / ELLIPSE,
// which are aiCallable:false) without mutating the document. Geometry is only
// ever changed by the reducer the returned commands flow into — never written
// here — honouring the CLAUDE.md rules:
//   "AI drafting … может только предлагать и исполнять команды через
//    безопасный контур." + "любые изменения геометрии только через Command
//    Engine."
//
// This is the FINAL CC-2c sub-arm — it completes the AI dispatcher matrix.
//
// The thin per-action translators live in the PACKAGE-INTERNAL sibling
// `basePrimTranslators.ts` (NOT re-exported by the barrel), so the ONLY public
// base-prim entry is the guarded `translateBasePrimCommand` below. This mirrors
// the BLOCK / ENTITY / DRAW-MODIFY domains, preventing a consumer of
// `@dxf-ai-editor/ai-commands` from emitting a geometry command past the
// fail-closed guard.

import type { EditorCommand } from "@dxf-ai-editor/contracts";
import {
  ALL_COMMAND_DESCRIPTORS,
  type CommandDescriptor,
} from "@dxf-ai-editor/command-registry";

import { type AiDispatchContext } from "./blockDispatcherShared";
import { assertAiCallableBasePrimAction } from "./basePrimGuard";
import type { BasePrimDomainAiCommand } from "./basePrimSchemas";
import {
  translateCopy,
  translateCreateArc,
  translateCreateCircle,
  translateCreateLine,
  translateCreatePline,
  translateDeleteEntities,
  translateMove,
  translateRotate,
} from "./basePrimTranslators";

// Re-export the base-prim safe-contour guard (mirrors blockDispatcher /
// drawModifyDispatcher re-exporting their guards) so consumers can reach it
// from this domain entry point.
export {
  BASE_PRIM_AI_ACTION_TARGET_COMMAND,
  assertAiCallableBasePrimAction,
} from "./basePrimGuard";

/**
 * Top-level base-primitive / base-modify dispatcher. Runs the CC-2a fail-closed
 * guard, then routes the AI command to the translator that owns its `action`.
 * The create commands resolve their `spaceId` from `context` (active space when
 * the AI omits one); the modify commands are self-contained (entity ids).
 * Returns the flat `EditorCommand[]` the store should dispatch (the caller wraps
 * the batch in one grouped-undo unit — see the app safe contour).
 */
export function translateBasePrimCommand(
  command: BasePrimDomainAiCommand,
  context: AiDispatchContext = {},
  descriptors: readonly CommandDescriptor[] = ALL_COMMAND_DESCRIPTORS,
): EditorCommand[] {
  // Safe-contour gate (CC-2a): refuse to translate any action whose target
  // command is not `aiCallable: true`. Fails closed before any EditorCommand is
  // produced. `descriptors` is injectable for tests.
  assertAiCallableBasePrimAction(command.action, descriptors);
  switch (command.action) {
    case "CREATE_LINE":
      return translateCreateLine(command, context);
    case "CREATE_CIRCLE":
      return translateCreateCircle(command, context);
    case "CREATE_ARC":
      return translateCreateArc(command, context);
    case "CREATE_PLINE":
      return translateCreatePline(command, context);
    case "MOVE":
      return translateMove(command);
    case "COPY":
      return translateCopy(command);
    case "ROTATE":
      return translateRotate(command);
    case "DELETE_ENTITIES":
      return translateDeleteEntities(command);
  }
}

/**
 * Batch variant — flatten-translates a list of base-prim / base-modify AI
 * commands in order via {@link translateBasePrimCommand} (each guarded
 * individually).
 */
export function translateBasePrimCommandBatch(
  commands: readonly BasePrimDomainAiCommand[],
  context: AiDispatchContext = {},
  descriptors: readonly CommandDescriptor[] = ALL_COMMAND_DESCRIPTORS,
): EditorCommand[] {
  const all: EditorCommand[] = [];
  for (const command of commands) {
    all.push(...translateBasePrimCommand(command, context, descriptors));
  }
  return all;
}
